import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { ModalContext } from './modalContext';
import { MODAL_REGISTRY } from './modalRegistry';
import type { ModalKey } from './modalKeys';
import type { ModalControls, ModalFrame, ModalPropsByKey, ModalRequest } from './modalTypes';

interface ActiveModal {
  request: ModalRequest;
  onClose: () => void;
}

/** Shows the registered component for one open modal, with the props it was opened with. */
const ActiveModalComponent: React.FC<ActiveModal> = ({ request, onClose }) => {
  // TypeScript can't relate request.key to request.props through the registry lookup, so the
  // component is widened to accept the request's props. ModalRequest keeps each pair matched.
  const Modal = MODAL_REGISTRY[request.key] as React.ComponentType<ModalRequest['props'] & ModalFrame>;
  return <Modal {...request.props} open onClose={onClose} />;
};

interface ModalProvider {
  children: React.ReactNode;
}

/**
 * Owns which modal is open for the whole app and renders it above the page. Components open one
 * through useModal; each modal keeps its own markup in its own component.
 */
const ModalProviderComponent: React.FC<ModalProvider> = ({ children }) => {
  // A list rather than a single request, so stacking modals later needs no change in shape.
  // Today opening a modal replaces whatever is open.
  const [requests, setRequests] = useState<ModalRequest[]>([]);

  // Stable identities are required: useConsentGate lists openModal among its effect
  // dependencies, so a new function on every open or close would re-run those effects.
  const openModal = useCallback(<K extends ModalKey>(key: K, props: ModalPropsByKey[K]) => {
    setRequests([{ key, props } as ModalRequest]);
  }, []);
  const closeModal = useCallback(() => setRequests([]), []);
  // One stable value, so every consumer doesn't re-render each time a modal opens or closes.
  const controls = useMemo<ModalControls>(() => ({ openModal, closeModal }), [openModal, closeModal]);

  return (
    <ModalContext.Provider value={controls}>
      {children}
      <Suspense fallback={null}>
        {requests.map((request) => (
          <ActiveModalComponent key={request.key} request={request} onClose={closeModal} />
        ))}
      </Suspense>
    </ModalContext.Provider>
  );
};

export default ModalProviderComponent;
